import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/design-system.css';
import '../styles/Dashboard.css';

function Dashboard() {
  const { user, logout } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});

  const resolveMediaUrl = (path) => {
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const apiBase = (api.defaults.baseURL || window.location.origin).replace(/\/$/, '');
    return `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
  };

  useEffect(() => { fetchBrands(); }, []);

  const fetchBrands = async () => {
    try {
      const response = await api.get('/api/brands');
      setBrands(response.data);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (brandId) => {
    setGenerating({ ...generating, [brandId]: true });
    try {
      await api.post(`/brand/${brandId}/generate`);
      alert('Post generated successfully!');
    } catch (error) {
      alert('Failed to generate post: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setGenerating({ ...generating, [brandId]: false });
    }
  };

  const handleDelete = async (brandId, brandName) => {
    if (!window.confirm(`Delete "${brandName}" and all its posts? This cannot be undone.`)) return;
    try {
      await api.post(`/brand/${brandId}/delete`);
      setBrands(brands.filter((b) => b.id !== brandId));
    } catch (error) {
      alert('Failed to delete brand: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="ps-loading">
        <div className="ps-spinner" />
        <span className="ps-loading-text">Loading your brands…</span>
      </div>
    );
  }

  return (
    <div className="ps-page">
      {/* ── Navbar ── */}
      <nav className="ps-nav">
        <div className="ps-container">
          <div className="ps-nav__inner">
            <Link to="/dashboard" className="ps-nav__logo">
              Postrify <span className="ps-nav__logo-dot" />
            </Link>
            <div className="ps-nav__right">
              <span className="ps-nav__user">👤 {user?.username}</span>
              <button onClick={logout} className="ps-btn ps-btn--secondary ps-btn--sm">Log out</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="ps-container">
        {/* ── Page Header ── */}
        <div className="ps-page-header">
          <div>
            <h1 className="ps-page-header__title">My Brands</h1>
            <p className="ps-page-header__sub">Manage your brands and generate AI-powered content</p>
          </div>
          <div className="ps-page-header__actions">
            <Link to="/brand/new" className="ps-btn ps-btn--primary">+ New Brand</Link>
          </div>
        </div>

        {/* ── Stats ── */}
        {brands.length > 0 && (
          <div className="dash-stats">
            <div className="stat-card">
              <div className="stat-card__icon">🏢</div>
              <div>
                <div className="stat-card__val">{brands.length}</div>
                <div className="stat-card__label">Active Brands</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card__icon">📱</div>
              <div>
                <div className="stat-card__val">{brands.filter(b => b.instagram_connected || b.facebook_connected).length}</div>
                <div className="stat-card__label">Connected Accounts</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card__icon">⚡</div>
              <div>
                <div className="stat-card__val">{brands.filter(b => b.auto_post_enabled).length}</div>
                <div className="stat-card__label">Auto-Posting</div>
              </div>
            </div>
          </div>
        )}

        {/* ── Brands Grid or Empty ── */}
        {brands.length === 0 ? (
          <div className="ps-empty">
            <div className="ps-empty__icon">🏢</div>
            <h2 className="ps-empty__title">No brands yet</h2>
            <p className="ps-empty__desc">Create your first brand to start generating AI-powered social media content.</p>
            <Link to="/brand/new" className="ps-btn ps-btn--primary ps-btn--lg">Create your first brand →</Link>
          </div>
        ) : (
          <div className="brand-grid">
            {brands.map((brand) => (
              <div key={brand.id} className="brand-card">
                <div className="brand-card__head">
                  {brand.logo_path ? (
                    <img src={resolveMediaUrl(brand.logo_path)} alt={`${brand.name} logo`} className="brand-card__logo" />
                  ) : (
                    <div className="brand-card__logo-placeholder">{brand.name[0].toUpperCase()}</div>
                  )}
                  <div className="brand-card__meta">
                    <div className="brand-card__name">{brand.name}</div>
                    <div className="brand-card__niche">{brand.niche}</div>
                    <div className="brand-card__tone">
                      <span className="ps-badge ps-badge--muted">{brand.tone}</span>
                    </div>
                  </div>
                </div>

                <div className="brand-card__divider" />

                <div className="brand-card__actions">
                  {/* Row 1: View Posts | Generate Post (Primary) */}
                  <Link to={`/brand/${brand.id}`} className="ps-btn ps-btn--secondary ps-btn--sm">View Posts</Link>
                  <button
                    onClick={() => handleGenerate(brand.id)}
                    className="ps-btn ps-btn--primary ps-btn--sm"
                    disabled={generating[brand.id]}
                  >
                    {generating[brand.id] ? 'Generating…' : '⚡ Generate Post'}
                  </button>
                  {/* Row 2: Edit | Settings */}
                  <Link to={`/brand/${brand.id}/edit`} className="ps-btn ps-btn--secondary ps-btn--sm">Edit</Link>
                  <Link to={`/brand/${brand.id}/settings`} className="ps-btn ps-btn--secondary ps-btn--sm">⚙ Settings</Link>
                  {/* Row 3: Delete (Full Width, Ghost Style) */}
                  <button
                    onClick={() => handleDelete(brand.id, brand.name)}
                    className="ps-btn ps-btn--danger-outline ps-btn--sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <footer className="ps-footer">
        <div className="ps-container">
          © 2024 Postrify · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

export default Dashboard;
